/**
 * AdminMediaLibraryPage - Iter 9.40+ Pass 4
 *
 * Single source of truth for every uploaded image asset across the platform.
 * Aggregates from master_products, master_brands, categories, organizations,
 * inspirations, master_intakes, master_experiences, users.
 *
 * Features:
 *   - Filter by source collection
 *   - See usage count + locations per asset
 *   - Click an asset → expand to see exact references
 *   - Replace a URL across every reference (atomic admin op)
 */
import { useEffect, useState, useMemo } from "react";
import { Loader2, Image as ImageIcon, RefreshCw, Search, X, Copy, ArrowUp } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABELS = {
  any: "All sources",
  master_products: "Products",
  master_brands: "Brands",
  categories: "Categories",
  master_intakes: "Universal options",
  organizations: "Hosts",
  inspirations: "Inspo",
  master_experiences: "Envelopes",
  users: "Avatars",
};

function AssetTile({ asset, onSelect, selected }) {
  const [imgOk, setImgOk] = useState(true);
  return (
    <button
      type="button"
      onClick={() => onSelect(asset)}
      data-testid={`media-tile-${asset.asset_id}`}
      className={`group relative rounded-xl overflow-hidden border-2 text-left transition-all ${
        selected ? "border-black shadow-md" : "border-stone-200 hover:border-stone-400"
      }`}
    >
      <div className="aspect-square bg-stone-100 flex items-center justify-center">
        {imgOk ? (
          <img
            src={asset.url}
            alt={asset.label || asset.asset_id}
            className="w-full h-full object-cover"
            onError={() => setImgOk(false)}
          />
        ) : (
          <div className="flex flex-col items-center gap-1 text-stone-400 text-[10px] px-2 text-center">
            <ImageIcon className="w-6 h-6" />
            <span className="break-all">{asset.url.split("/").slice(-1)[0]}</span>
          </div>
        )}
      </div>
      <div className="px-2 py-1.5 bg-white">
        <div className="text-[11px] font-medium text-stone-900 truncate">
          {asset.label || asset.asset_id}
        </div>
        <div className="text-[10px] text-stone-500">
          Used {asset.usage_count}×
        </div>
      </div>
      <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-black/70 text-white text-[9px] rounded">
        {asset.sources[0]?.source.replace("master_", "").slice(0, 9)}
      </div>
    </button>
  );
}

function AssetDetailPanel({ asset, onClose, onReplace }) {
  const [newUrl, setNewUrl] = useState("");
  if (!asset) return null;
  return (
    <div className="fixed top-0 right-0 bottom-0 w-[420px] bg-white border-l border-stone-200 shadow-xl flex flex-col z-40" data-testid="media-detail-panel">
      <div className="p-4 border-b border-stone-200 flex items-center justify-between">
        <h3 className="text-sm font-semibold">Asset details</h3>
        <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded" data-testid="media-detail-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        <div className="aspect-square rounded-xl bg-stone-100 overflow-hidden border border-stone-200">
          <img src={asset.url} alt={asset.label} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-1">Label</div>
          <div className="text-sm font-medium">{asset.label || "-"}</div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-1">URL</div>
          <div className="text-xs font-mono break-all text-stone-700 bg-stone-50 p-2 rounded flex items-start gap-2">
            <span className="flex-1">{asset.url}</span>
            <button
              onClick={() => { navigator.clipboard.writeText(asset.url); toast.success("URL copied"); }}
              data-testid="media-copy-url"
              className="text-stone-400 hover:text-black"
            >
              <Copy className="w-3 h-3" />
            </button>
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-1">Used in {asset.usage_count} place{asset.usage_count !== 1 ? "s" : ""}</div>
          <div className="space-y-1.5">
            {asset.sources.map((s, i) => (
              <div key={i} className="text-xs bg-stone-50 rounded p-2">
                <div className="font-medium text-stone-900">{SOURCE_LABELS[s.source] || s.source}</div>
                <div className="text-stone-500">{s.doc_id} · <span className="font-mono">{s.location}</span></div>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-stone-200">
          <div className="text-[11px] uppercase tracking-wide text-stone-500 mb-2">Replace across all {asset.usage_count} references</div>
          <input
            type="url"
            placeholder="https://new-image-url.png"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-stone-300 rounded-lg mb-2"
            data-testid="media-replace-input"
          />
          <button
            type="button"
            disabled={!newUrl}
            onClick={() => onReplace(asset.url, newUrl)}
            data-testid="media-replace-btn"
            className="w-full px-3 py-2 text-sm bg-black text-white rounded-lg hover:bg-stone-800 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            Replace everywhere
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminMediaLibraryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("any");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/media-library?source=${source}&limit=500`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setData(await res.json());
    } catch (e) {
      toast.error(`Failed to load media library: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [source]);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    if (!query) return data.items;
    const q = query.toLowerCase();
    return data.items.filter((it) =>
      (it.label || "").toLowerCase().includes(q) ||
      it.url.toLowerCase().includes(q) ||
      it.sources.some((s) => s.doc_id?.toLowerCase().includes(q))
    );
  }, [data, query]);

  const handleReplace = async (oldUrl, newUrl) => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/admin/media-library/replace`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ old_url: oldUrl, new_url: newUrl }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const d = await res.json();
      toast.success(`Replaced in ${d.replaced} place(s)`);
      setSelected(null);
      load();
    } catch (e) {
      toast.error(`Replace failed: ${e.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-media-library-page">
      <div className={`p-6 max-w-7xl mx-auto ${selected ? "pr-[440px]" : ""}`}>
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900">Media Library</h1>
            <p className="text-sm text-stone-500 mt-1">
              Every image asset across products, brands, categories, hosts, and users.
            </p>
          </div>
          <button
            onClick={load}
            data-testid="media-refresh-btn"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white border border-stone-300 rounded-lg hover:border-black"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>

        {/* Breakdown */}
        {data?.breakdown && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="media-breakdown">
            <button
              onClick={() => setSource("any")}
              data-testid="media-filter-any"
              className={`px-3 py-1.5 text-xs rounded-full border ${source === "any" ? "bg-black text-white border-black" : "bg-white border-stone-300 hover:border-black"}`}
            >
              All · {data.total}
            </button>
            {Object.entries(data.breakdown).map(([src, count]) => (
              <button
                key={src}
                onClick={() => setSource(src)}
                data-testid={`media-filter-${src}`}
                className={`px-3 py-1.5 text-xs rounded-full border ${source === src ? "bg-black text-white border-black" : "bg-white border-stone-300 hover:border-black"}`}
              >
                {SOURCE_LABELS[src] || src} · {count}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            placeholder="Search by label, URL, or doc id…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="media-search-input"
            className="w-full pl-10 pr-4 py-2 text-sm border border-stone-300 rounded-lg focus:outline-none focus:border-black"
          />
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="media-loading">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-sm text-stone-500" data-testid="media-empty">
            No assets match this filter.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="media-grid">
            {filtered.map((asset) => (
              <AssetTile
                key={asset.asset_id}
                asset={asset}
                selected={selected?.asset_id === asset.asset_id}
                onSelect={setSelected}
              />
            ))}
          </div>
        )}
      </div>

      <AssetDetailPanel
        asset={selected}
        onClose={() => setSelected(null)}
        onReplace={handleReplace}
      />
    </div>
  );
}
